<template>
  <div
    v-if="vmShowShareModal"
    class="modal-wrapper"
    ref="modalRef"
    @click.stop=""
  >
    <div class="flex w-full items-center justify-between text-primary">
      <span class="font-bold">Share it</span>
      <button @click.stop="closeModal">
        <BcIcon
          icon="x"
          color="white"
          size="16px"
          class="m-2"
        />
      </button>
    </div>
    <ul class="flex gap-x-2">
      <li
        v-for="[key, value] of Object.entries(SOCIAL_SHARE)"
        :key="key"
      >
        <button
          class="flex items-center justify-center rounded-lg bg-primary/5 p-4"
          @click.stop="share(value.shareUrl)"
        >
          <BcIcon
            :icon="value.icon"
            color="white"
            size="28px"
          />
        </button>
      </li>
    </ul>
    <div class="copy-wrapper">
      <input
        type="text"
        :value="shareUrl"
        class="url-input"
        readonly
      >
      <button
        class="copy-button w-[64px]"
        @click.stop="copyShareUrl"
      >
        <BcIcon
          v-show="isCopied"
          icon="check"
          color="white"
          size="21px"
        />
        <span v-show="!isCopied">Copy</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside, useVModel } from '@vueuse/core'
import { BcIcon } from 'xangle-ui'

import { SOCIAL_SHARE } from '~/constants/social.constant'

interface IProps {
  modelValue: boolean
  shareUrl: string
}

const props = defineProps<IProps>()

let copyTimer: NodeJS.Timeout
const modalRef = ref<HTMLDivElement | null>(null)
const isCopied = ref(false)

const route = useRoute()
const vmShowShareModal = useVModel(props, 'modelValue')

const closeModal = () => {
  vmShowShareModal.value = false
}

const copy = async (value: string) => {
  await window.navigator.clipboard.writeText(value)
}

const copyShareUrl = async () => {
  await copy(props.shareUrl)
  isCopied.value = true
}

const share = (socialShareUrl: string) => {
  window.open(`${socialShareUrl}${encodeURIComponent(props.shareUrl)}`)
  closeModal()
}

onClickOutside(modalRef, () => {
  closeModal()
})

onBeforeMount(() => {
  clearTimeout(copyTimer)
  isCopied.value = false
})

watch(() => isCopied.value, () => {
  if (isCopied.value) {
    copyTimer = setTimeout(() => {
      isCopied.value = false
    }, 2000)
  }
})
</script>

<style scoped lang="scss">
.modal-wrapper {
  @apply w-fit flex flex-col items-center gap-y-2 bg-primary/10 p-4 border border-primary/20 rounded-lg
  shadow-primary/20 shadow-[0px_16px_32px] text-primary backdrop-blur-3xl cursor-default;

  .copy-wrapper {
    @apply flex justify-center gap-x-1 w-full;

    .url-input {
      @apply bg-transparent rounded-lg bg-primary/5 w-full outline-none p-2 text-base;
    }

    .copy-button {
      @apply flex justify-center items-center bg-explorer-blue p-2 rounded-lg font-bold text-lg;
    }
  }
}
</style>